import React, { useCallback, useContext, useEffect, useState } from "react";

import { useSearchParams } from "react-router-dom";

import {
  BadgeNotification,
  Button,
  Caption,
  Checkbox,
  DetailedSubheading,
  Flex,
  InputSearch,
  Modal,
} from "@olist/design-system";
import { AdvancedFilterOutline } from "@olist/design-system-icons";
import { themes } from "@olist/design-system-tokens";

import { MixpanelContext } from "contexts";
import { productOptions } from "helpers/formatters";
import { useCustomNavigate } from "utils";

export function WikiJsSearch() {
  const { trackAgent } = useContext(MixpanelContext);
  const navigate = useCustomNavigate();
  const [searchParams] = useSearchParams();
  const product = searchParams.get("product");

  const [searchValue, setSearchValue] = useState("");
  const [selectedFilter, setSelectedFilter] = useState("");
  const [productFilter, setProductFilter] = useState("");
  const [isOpen, setIsOpen] = React.useState(false);

  const handleOpen = () => setIsOpen(!isOpen);
  const resetFilter = () => {
    setProductFilter("");
    setSelectedFilter("");
    handleOpen();
  };
  const addFilter = () => {
    const filter = selectedFilter;
    setProductFilter(filter);
    handleOpen();
  };

  const handleSearch = useCallback(
    (searchedValue: string) => {
      if (!searchedValue) return;
      trackAgent.track(`Central de ajuda - Pesquisa`, {
        asset: "Central de ajuda",
        page: "search",
        action: "search",
        filtro: productFilter,
        searchedText: searchedValue,
      });

      const encodedValue = encodeURIComponent(searchedValue);
      const searchUrl = `/kb/search?search=${encodedValue}&product=${productFilter}`;

      navigate(searchUrl, true);
    },
    [navigate]
  );

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Enter") {
        const activeElement = document.activeElement as HTMLElement;
        if (
          activeElement &&
          activeElement.tagName === "INPUT" &&
          activeElement.getAttribute("type") === "search"
        ) {
          handleSearch(searchValue);
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [searchValue, handleSearch]);

  useEffect(() => {
    if (product && productFilter === "") {
      setProductFilter(product);
      setSelectedFilter(product);
    }
  }, [product]);

  return (
    <>
      <DetailedSubheading
        type="default"
        color={themes.olist.base.color.neutral.softest}
        pb={16}
      >
        Como podemos te ajudar?
      </DetailedSubheading>
      <Flex gap="inline.medium" alignItems="center">
        <InputSearch
          ctaButton
          ctaButtonVariation="secondary"
          value={searchValue}
          placeholder="Pesquisar"
          onClean={() => setSearchValue("")}
          onSearch={handleSearch}
          onChange={(event) => setSearchValue(event.target.value)}
        />
        <Button
          variation="secondary"
          onClick={handleOpen}
          iconLeft={AdvancedFilterOutline}
          style={{ height: "48px", marginTop: "8px" }}
        >
          {productFilter !== "" ? (
            <BadgeNotification countNotifications={1}>
              filtrar
            </BadgeNotification>
          ) : (
            <Caption>filtrar</Caption>
          )}
        </Button>
      </Flex>
      <Modal
        isOpen={isOpen}
        onClose={handleOpen}
        title="filtrar por produto"
        size="small"
        body={
          <Flex gap="inline.medium">
            <Caption bold paddingTop="stack.1xsmall">
              produtos
            </Caption>
            <Flex gap="inline.medium" flexWrap="wrap">
              {productOptions.map(({ label, value }) => (
                <Checkbox
                  key={value}
                  label={label}
                  value={value}
                  checked={value === selectedFilter}
                  onChange={() => setSelectedFilter(value)}
                />
              ))}
            </Flex>
          </Flex>
        }
        footer={
          <Flex justifyContent="space-between" width="100%">
            <Button variation="secondary" onClick={handleOpen}>
              cancelar
            </Button>
            <Flex gap="inline.medium">
              <Button variation="secondary" onClick={resetFilter}>
                limpar
              </Button>
              <Button onClick={addFilter}>adicionar filtro</Button>
            </Flex>
          </Flex>
        }
      />
    </>
  );
}
